import User from "@/data/models/User";

interface ToolbarClientLike {
  shortName?: string | null;
  companyName?: string | null;
  clientName?: string | null;
}

/**
 * Portal target of the phone app bar's ⋮ menu (Scaffold). Screens send their text actions there
 * through `ToolbarOverflowItem` below 600 px, where the bar has no room for labelled buttons.
 */
export const TOOLBAR_OVERFLOW_PORTAL = "toolbar-overflow";

/**
 * Returns the best client label for toolbar context.
 * Priority: short name -> company name -> client name.
 */
export function getToolbarContextClientName(clientInfo?: ToolbarClientLike | null): string | null {
  if (!clientInfo) {
    return null;
  }

  if (clientInfo.shortName) {
    return clientInfo.shortName;
  }

  if (clientInfo.companyName) {
    return clientInfo.companyName;
  }

  if (clientInfo.clientName) {
    return clientInfo.clientName;
  }

  return null;
}

/**
 * Returns the toolbar user label: who is signed in. "Sicht auf" has its own chip in the app bar
 * (Scaffold, ticket 2610050), so this never names the viewed user.
 */
export function getToolbarContextUserText(
  activeUser: User | null,
  translate: (key: string, params: string[]) => string,
): string {
  const userName = activeUser ? activeUser.nickname : "Master";

  return translate("common.signedInAs", [userName]);
}

/**
 * Fallback should appear only when both right-side action areas are empty.
 */
export function shouldShowToolbarContextFallback(
  hasToolbarContentActions: boolean,
  hasToolbarMenuActions: boolean,
): boolean {
  return !hasToolbarContentActions && !hasToolbarMenuActions;
}

/**
 * Returns whether fallback should be rendered with explicit resolved-state gating.
 */
export function shouldRenderToolbarContextFallback(
  toolbarActionsResolved: boolean,
  hasToolbarContentActions: boolean,
  hasToolbarMenuActions: boolean,
  showClientUserContextFallback: boolean,
): boolean {
  if (!toolbarActionsResolved) {
    return false;
  }

  if (!showClientUserContextFallback) {
    return false;
  }

  return shouldShowToolbarContextFallback(
    hasToolbarContentActions,
    hasToolbarMenuActions,
  );
}

/**
 * Determines if an element contains visible toolbar action content.
 */
export function hasRenderableToolbarContent(element?: HTMLElement | null): boolean {
  if (!element) {
    return false;
  }

  if (element.querySelector("button, input, select, textarea, a, .v-btn, .v-icon")) {
    return true;
  }

  return (element.textContent || "").trim().length > 0;
}
